import { QuickExceptionButton } from "@/components/landing/CreateLandingExceptionButton";
import { PricedFlightDetail } from "@/components/landing/PricedFlightDetail";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { PricedFlight } from "@/types/landing";
import { AlertTriangle, Ban, Copy, RotateCcw } from "lucide-react";
import React, { useState } from "react";
import { TailNumberOverrideDialog } from "./TailNumberOverrideDialog";

export function PricedFlightCardList({ flights }: { flights: PricedFlight[] }) {
  return (
    <div className="flex flex-col space-y-4">
      {flights.map((f) => (
        <PricedFlightDetail flight={f} key={f.id} />
      ))}
    </div>
  );
}

const formatMoney = (cents: number) => {
  return `$${(cents / 100).toFixed(2)}`;
};

const formatWeight = (lbs: number | null) => {
  if (!lbs) return "N/A";
  return `${lbs.toLocaleString()} lbs`;
};

const StatusBadge: React.FC<{ type: string }> = ({ type }) => {
  const getStatusColor = (flightType: string) => {
    switch (flightType) {
      case "Scheduled":
        return "bg-green-400/10 text-green-400 ring-green-400/20";
      case "GA":
        return "bg-blue-400/10 text-blue-400 ring-blue-400/20";
      case "Unscheduled":
        return "bg-yellow-400/10 text-yellow-400 ring-yellow-400/20";
      default:
        return "bg-gray-400/10 text-gray-400 ring-gray-400/20";
    }
  };

  return (
    <span
      className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset ${getStatusColor(type)}`}
    >
      {type}
    </span>
  );
};

const FeeTierBadge: React.FC<{ tier: string; isSignatory: boolean }> = ({
  tier,
  isSignatory,
}) => {
  if (isSignatory) {
    return (
      <span className="inline-flex items-center rounded-md bg-green-400/10 px-2 py-1 text-xs font-medium text-green-400 ring-1 ring-inset ring-green-400/20">
        Signatory
      </span>
    );
  }

  const getFeeTierColor = (feeTier: string) => {
    switch (feeTier) {
      case "general_aviation":
        return "bg-blue-400/10 text-blue-400 ring-blue-400/20";
      case "non_signatory":
        return "bg-yellow-400/10 text-yellow-400 ring-yellow-400/20";
      default:
        return "bg-gray-400/10 text-gray-400 ring-gray-400/20";
    }
  };

  return (
    <span
      className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset ${getFeeTierColor(tier)}`}
    >
      {tier
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ")}
    </span>
  );
};

const DiversionBadge: React.FC<{ flight: PricedFlight }> = ({ flight }) => {
  if (!flight.diversion) return null;

  if (flight.diverted_back) {
    return (
      <span className="inline-flex items-center gap-1 rounded-md bg-orange-400/10 px-2 py-1 text-xs font-medium text-orange-400 ring-1 ring-inset ring-orange-400/20">
        <RotateCcw className="h-3 w-3" />
        Returned
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-1 rounded-md bg-purple-400/10 px-2 py-1 text-xs font-medium text-purple-400 ring-1 ring-inset ring-purple-400/20">
      <AlertTriangle className="h-3 w-3" />
      Diverted
    </span>
  );
};

const IgnoredBadge: React.FC<{ flight: PricedFlight }> = ({ flight }) => {
  if (!flight.ignored) return null;

  return (
    <span className="inline-flex items-center gap-1 rounded-md bg-red-400/10 px-2 py-1 text-xs font-medium text-red-400 ring-1 ring-inset ring-red-400/20">
      <Ban className="h-3 w-3" />
      Ignored
    </span>
  );
};

interface FlightDetailTableProps {
  flights: PricedFlight[];
}

export function FlightDetailTable({ flights }: FlightDetailTableProps) {
  const [selectedRawData, setSelectedRawData] = useState<string | null>(null);

  const handleRowDoubleClick = (flight: PricedFlight) => {
    if (flight.raw_data) {
      try {
        // If the data is already a string, parse it first
        const parsed =
          typeof flight.raw_data === "string"
            ? JSON.parse(flight.raw_data)
            : flight.raw_data;
        // Then stringify it with proper formatting
        setSelectedRawData(JSON.stringify(parsed, null, 2));
      } catch (e) {
        // Fallback to raw string if parsing fails
        setSelectedRawData(String(flight.raw_data));
      }
    }
  };

  const handleCopyClick = () => {
    if (selectedRawData) {
      navigator.clipboard.writeText(selectedRawData);
    }
  };

  return (
    <>
      <div className="flow-root border-gray-600 border bg-slate-900 rounded-lg shadow-lg p-6 w-full text-slate-200">
        <div className="overflow-x-auto">
          <div className="inline-block min-w-full align-middle">
            <table className="min-w-full divide-y divide-gray-700">
              <thead>
                <tr>
                  <th
                    scope="col"
                    className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-200 sm:pl-0"
                  >
                    Flight Info
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-3.5 text-left text-sm font-semibold text-gray-200"
                  >
                    Aircraft
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-3.5 text-left text-sm font-semibold text-gray-200"
                  >
                    Carrier
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-3.5 text-left text-sm font-semibold text-gray-200"
                  >
                    Route
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-3.5 text-left text-sm font-semibold text-gray-200"
                  >
                    Weights
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-3.5 text-left text-sm font-semibold text-gray-200"
                  >
                    Tail Number
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-3.5 text-left text-sm font-semibold text-gray-200"
                  >
                    Status
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-3.5 text-right text-sm font-semibold text-gray-200"
                  >
                    Fee
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-3.5 text-center text-sm font-semibold text-gray-200"
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800">
                {flights.map((flight) => (
                  <tr
                    key={`${flight.departure_time_display}-${flight.flight_number_display}-${flight.tail_number}`}
                    className={`${flight.ignored ? "opacity-75" : ""}`}
                    onDoubleClick={() => handleRowDoubleClick(flight)}
                  >
                    {/* Flight Info */}
                    <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm sm:pl-0">
                      <div className="font-medium text-gray-200">
                        {flight.flight_number_display || "GA Flight"}
                      </div>
                      <div className="mt-1 text-gray-400">
                        DEP: {flight.departure_time_display}
                      </div>
                      <div className="mt-1 text-gray-400">
                        ARR: {flight.arrival_time_display}
                      </div>
                    </td>

                    {/* Aircraft */}
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-300">
                      <div>
                        {flight.aircraft_code && `${flight.aircraft_code}`}
                      </div>
                    </td>

                    {/* Carrier */}
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-300">
                      <div className="flex items-center gap-2">
                        {flight.carrier_image && (
                          <img
                            src={flight.carrier_image}
                            className="w-6 h-6"
                            onError={(e) =>
                              (e.currentTarget.style.display = "none")
                            }
                          />
                        )}
                        <span>{flight.carrier_name}</span>
                      </div>
                    </td>

                    {/* Route */}
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-300">
                      <div className="font-mono">
                        {flight.departure_airport_code} →{" "}
                        {flight.arrival_airport_code}
                      </div>
                      {flight.diversion && (
                        <div className="text-gray-400 mt-1 text-xs">
                          {flight.diverted_back
                            ? "Returned to departure"
                            : flight.diverted_away
                              ? `Diverted from ${flight.departure_airport_code}`
                              : null}
                        </div>
                      )}
                    </td>

                    {/* Weights */}
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-300">
                      <div>MTOW: {formatWeight(flight.mtow_lbs)}</div>
                      <div className="mt-1">
                        MGLW: {formatWeight(flight.mlw_lbs)}
                      </div>
                    </td>

                    {/* Tail Number */}
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-300">
                      <div className="flex items-center gap-2">
                        <span>
                          {flight.tail_number ||
                            flight.effective_tail_number ||
                            "N/A"}
                        </span>
                        {!flight.tail_number &&
                          !flight.effective_tail_number && (
                            <TailNumberOverrideDialog flight={flight} />
                          )}
                      </div>
                    </td>

                    {/* Status */}
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-300">
                      <div className="flex flex-col gap-2">
                        <StatusBadge type={flight.flight_type} />
                        <FeeTierBadge
                          tier={flight.fee_tier}
                          isSignatory={flight.is_signatory}
                        />
                        <DiversionBadge flight={flight} />
                        <IgnoredBadge flight={flight} />
                      </div>
                    </td>

                    {/* Fee */}
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-right">
                      {flight.overridden_fee_cents !== null &&
                      flight.overridden_fee_cents !== undefined ? (
                        <div className="flex flex-col items-end gap-1">
                          <span className={`line-through text-gray-500`}>
                            {formatMoney(flight.landing_fee_cents)}
                          </span>
                          <span
                            className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset ${flight.overridden_fee_cents === 0 ? "bg-orange-400/10 text-orange-400 ring-orange-400/20" : "bg-amber-400/10 text-amber-400 ring-amber-400/20"}`}
                          >
                            {formatMoney(flight.overridden_fee_cents)}
                          </span>
                        </div>
                      ) : (
                        <span
                          className={`font-medium ${flight.landing_fee_cents === 0 ? "text-orange-400" : "text-green-400"}`}
                        >
                          {formatMoney(flight.landing_fee_cents)}
                        </span>
                      )}
                    </td>

                    <td className="whitespace-nowrap px-3 py-4 text-sm text-center">
                      <QuickExceptionButton
                        airlineCodeIata={flight.carrier}
                        tailNumber={flight.tail_number}
                        flightName={
                          flight.flight_number_display ||
                          `${flight.departure_airport_code} to ${flight.arrival_airport_code}`
                        }
                        buttonSize="sm"
                        flight={flight}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <Dialog
        open={!!selectedRawData}
        onOpenChange={() => setSelectedRawData(null)}
      >
        <DialogContent className="max-w-4xl max-h-[80vh] bg-slate-900 text-slate-200">
          <DialogHeader className="flex flex-row items-center justify-between">
            <DialogTitle>Raw Flight Data</DialogTitle>
            <button
              onClick={handleCopyClick}
              className="flex items-center gap-2 px-3 py-1 text-sm rounded hover:bg-slate-800"
            >
              <Copy className="w-4 h-4" />
              Copy JSON
            </button>
          </DialogHeader>
          <ScrollArea className="h-[calc(80vh-120px)]">
            <pre className="p-4 text-sm font-mono">{selectedRawData}</pre>
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </>
  );
}
